import { ListOutput } from '@allocab/allocab-commons-shared-types';
import { SearchTripV2Input, TripOutput } from '@allocab/allocab-passenger-shared-types';
import type { FetchInfiniteQueryOptions } from '@tanstack/query-core';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { queryKeys } from '../services/datastore/queryKeys';
import { DEFAULT_CACHE_TIME, queryClient } from '../services/datastore/react-query';
import { passengerApi } from '../services/network/Api';
import { InfiniteQueryOptions, QueryOptions } from '../services/types';

export const LIMIT = 10;

export const DEFAULT_GET_TRIPS_CONFIG: SearchTripV2Input = {
  limit: LIMIT,
  offset: 0,
  orderBy: 'resume.departureAt',
  direction: 'desc',
};

export type Input = Omit<SearchTripV2Input, 'offset' | 'limit'> & { limit?: number };

export const fetchTrips = (input: SearchTripV2Input) =>
  passengerApi.post<SearchTripV2Input, TripOutput, 'list'>('/v2/trip/search', input);

export const useFetchTrips = (
  input: Input,
  options: InfiniteQueryOptions<typeof queryKeys.user.fetchTrips, ListOutput<TripOutput>> = {},
) => {
  const limit = input.limit || LIMIT;
  const fetchTripsQuery = useInfiniteQuery(
    queryKeys.user.fetchTrips(input),
    ({ pageParam = 0 }: { pageParam?: number }) => fetchTrips({ offset: pageParam, limit, ...input }),
    {
      getNextPageParam: (lastPage, allPages) => (lastPage.nbItems < limit ? undefined : limit * allPages.length),
      ...options,
    },
  );
  return fetchTripsQuery;
};

export const usePrefetchTrips = (
  input: SearchTripV2Input,
  options?: FetchInfiniteQueryOptions<ListOutput<TripOutput>>,
) => {
  useEffect(() => {
    queryClient.prefetchInfiniteQuery(queryKeys.user.fetchTrips(input), () => fetchTrips(input), options);
  }, [input, options]);
};

export const useFetchTripsPage = (
  input: Input,
  pageNumber = 1,
  options: QueryOptions<typeof queryKeys.user.fetchTripsPage, ListOutput<TripOutput>> = {},
) => {
  const apiInput: SearchTripV2Input = { ...DEFAULT_GET_TRIPS_CONFIG, ...input, offset: (pageNumber - 1) * LIMIT };
  const queryKey = queryKeys.user.fetchTripsPage(apiInput);
  return useQuery(queryKey, () => fetchTrips(apiInput), options);
};

export const prefetchTripsPage = (input: Input, pageNumber = 1) => {
  const apiInput: SearchTripV2Input = { ...DEFAULT_GET_TRIPS_CONFIG, ...input, offset: (pageNumber - 1) * LIMIT };

  queryClient.prefetchQuery(queryKeys.user.fetchTripsPage(apiInput), () => fetchTrips(apiInput), {
    staleTime: DEFAULT_CACHE_TIME,
  });
};

export const usePrefetchTripsPage = (input: Input, pageNumber = 1) => {
  useEffect(() => {
    prefetchTripsPage(input, pageNumber);
  }, [input, pageNumber]);
};
